/**
 * The embedded form/field builder — Step 29, folded into wherever a form is
 * actually needed (the catalogue item editor) rather than living as its own
 * top-level page (Kai: "there's 4 settings pages to do what 1 should do").
 * A layout is created implicitly the first time its owner saves with at
 * least one field — there is no separate "create a layout" step to skip.
 *
 * Sections hold fields FROM THE SHARED LIBRARY with per-layout settings
 * (required/audience/filterable). `showWhen` lives ONCE per section, not
 * per field (Kai, from the reference screenshots: dynamic SECTIONS, not
 * per-field conditions) — resolution on the server propagates a section's
 * condition onto every field inside it, so the flat validator/renderer
 * never had to change. Reordering is up/down buttons rather than full
 * drag-and-drop — a deliberate, stated simplification.
 *
 * Fields are added via a TYPE TOOLBAR (Kai: "I prefer this layout", pointing
 * at Freshservice's per-type icon row) — clicking a type creates a field of
 * that type and adds it in one action. There is deliberately NO "pick an
 * existing field" dropdown (Kai: "I don't want a list of existing fields");
 * reuse still happens because the field created here is the same shared
 * `field_definitions` row usable elsewhere, just never browsed as a list in
 * this flow. Each field row's pencil opens its FULL config (label, help
 * text, placeholder, options) — the piece the quick-create toolbar
 * deliberately doesn't ask for up front (Kai: "where do I change all the
 * settings like what field type, and the description, auto fill, title").
 */
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Eye,
  Pencil,
  Type,
  AlignLeft,
  FileText,
  Hash,
  Percent,
  ListFilter,
  ListChecks,
  CircleDot,
  CheckSquare,
  Calendar,
  CalendarClock,
  Link2,
  Mail,
  Phone,
  Paperclip,
  User,
  List,
  ArrowUpDown,
  Database,
  Library,
} from 'lucide-react';
import { fieldsApi, formLayoutsApi, type LayoutSectionInput, type LayoutSectionFieldInput } from './api';
import { FieldRenderer } from './FieldRenderer';
import { OptionsEditor, PeopleFilterEditor, TYPE_LABELS } from './FieldConfigControls';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Sheet } from '../../components/ui/sheet';
import { qk } from '../../lib/queryClient';
import {
  FIELD_TYPES,
  type FieldDefinition,
  type ResolvedField,
  type Condition,
  type FieldType,
  type SelectOption,
} from '@itsm/shared';
import { PREFILL_TOKENS } from '@itsm/shared';

/** What the sticky header's split-Save drives — the parent owns WHEN to
 *  save (Save & Publish / Save as Draft are ONE action over item + form);
 *  this editor owns HOW. Returns the layout id (created or existing), or
 *  null when the write failed. */
export interface FormLayoutEditorHandle {
  save: (name: string) => Promise<string | null>;
}

export const FormLayoutEditor = forwardRef<
  FormLayoutEditorHandle,
  {
    layoutId: string | null;
    onDirtyChange?: (dirty: boolean) => void;
  }
>(function FormLayoutEditor({ layoutId, onDirtyChange }, ref) {
  const queryClient = useQueryClient();
  const { data: layouts } = useQuery({ queryKey: qk.formLayouts, queryFn: formLayoutsApi.list });
  // Step 94: the item editor picks from fields REUSABLE ACROSS ITEMS, not
  // from the ticket form's fields and not from other items' one-off
  // questions. Distinct query key so it cannot share a cache entry with the
  // Ticket fields page, which asks for a different scope.
  const { data: library } = useQuery({
    queryKey: qk.sharedFieldDefinitions,
    queryFn: () => fieldsApi.list('shared'),
  });
  /**
   * Every definition, for RESOLVING a row — not for the picker.
   *
   * A question created inline here is scoped to its item (Step 94: one-off
   * questions must not silently join the shared library — 64 of them once
   * did). The picker above is therefore right to offer only `shared`, but the
   * ROW LOOKUP was using the same list, so a freshly created question could
   * not be found: its row rendered the raw UUID instead of the label, and its
   * pencil opened nothing. Kai, 2026-09-08: *"when I make a question it
   * doesn't keep the name I set, I can't edit it"*. It was always saved
   * correctly — the editor just could not see it.
   */
  const { data: allDefs } = useQuery({
    queryKey: qk.allFieldDefinitions,
    queryFn: () => fieldsApi.list('all'),
  });

  const [sections, setSections] = useState<LayoutSectionInput[]>([{ title: 'Details', fields: [] }]);
  // Library fields not already on this layout, archived ones excluded — the
  // pool the "Add shared field" picker offers.
  const sharedCandidates = useMemo(() => {
    const onLayout = new Set(sections.flatMap((sec) => sec.fields.map((f) => f.fieldId)));
    return (library?.entries ?? []).filter((f) => f.isActive !== 0 && !onLayout.has(f.id));
  }, [library, sections]);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [previewValues, setPreviewValues] = useState<Record<string, unknown>>({});
  const [previewSurface, setPreviewSurface] = useState<'agent' | 'requester'>('agent');
  const [dirty, setDirtyState] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const editingField = allDefs?.entries.find((f) => f.id === editingFieldId) ?? null;
  const setDirty = (d: boolean) => {
    setDirtyState(d);
    onDirtyChange?.(d);
  };

  useEffect(() => {
    if (!layoutId || loadedFor === layoutId) return;
    const l = layouts?.entries.find((x) => x.id === layoutId);
    if (l) {
      setSections(l.sections?.length ? l.sections : [{ title: 'Details', fields: [] }]);
      setLoadedFor(layoutId);
      setDirty(false);
    }
  }, [layoutId, layouts, loadedFor]);

  const { data: resolved } = useQuery({
    queryKey: qk.formLayoutPreview(layoutId ?? 'new', JSON.stringify(sections), previewSurface),
    queryFn: async () => {
      if (!layoutId) return { fields: [] as ResolvedField[] };
      return formLayoutsApi.resolved(layoutId, previewSurface);
    },
    enabled: !!layoutId,
  });

  // The item's ONE lifecycle flag is catalogue_items.isActive — layouts from
  // this flow always save active; drafts are drafts at the ITEM level.
  useImperativeHandle(ref, () => ({
    save: async (name: string) => {
      const body = { name, appliesTo: 'service_request', sections, isActive: true };
      const res = layoutId ? await formLayoutsApi.update(layoutId, body) : await formLayoutsApi.create(body);
      if (!res.ok) return null;
      void queryClient.invalidateQueries({ queryKey: qk.formLayouts });
      setDirty(false);
      const newId = layoutId ?? (res.body as { id: string }).id;
      if (!layoutId) setLoadedFor(newId);
      return newId;
    },
  }));

  function mutate(fn: (prev: LayoutSectionInput[]) => LayoutSectionInput[]) {
    setSections(fn);
    setDirty(true);
  }
  function addSection() {
    mutate((prev) => [...prev, { title: `Section ${prev.length + 1}`, fields: [] }]);
  }
  function updateSection(i: number, patch: Partial<LayoutSectionInput>) {
    mutate((prev) => prev.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  }
  function setSectionCondition(i: number, condition: Condition | undefined) {
    mutate((prev) =>
      prev.map((s, j) => {
        if (j !== i) return s;
        const { showWhen: _drop, ...rest } = s;
        return condition ? { ...rest, showWhen: condition } : rest;
      }),
    );
  }
  function removeSection(i: number) {
    mutate((prev) => (prev[i]?.fields.length ? prev : prev.filter((_, j) => j !== i)));
  }
  function addFieldToSection(sectionIndex: number, fieldId: string) {
    mutate((prev) =>
      prev.map((s, j) =>
        j === sectionIndex
          ? {
              ...s,
              fields: [
                ...s.fields,
                { fieldId, required: false, audience: 'both', filterable: false } as LayoutSectionFieldInput,
              ],
            }
          : s,
      ),
    );
  }
  function updateField(sectionIndex: number, fieldIndex: number, patch: Partial<LayoutSectionFieldInput>) {
    mutate((prev) =>
      prev.map((s, j) =>
        j === sectionIndex
          ? { ...s, fields: s.fields.map((f, k) => (k === fieldIndex ? { ...f, ...patch } : f)) }
          : s,
      ),
    );
  }
  function removeField(sectionIndex: number, fieldIndex: number) {
    mutate((prev) =>
      prev.map((s, j) =>
        j === sectionIndex ? { ...s, fields: s.fields.filter((_, k) => k !== fieldIndex) } : s,
      ),
    );
  }
  function moveField(sectionIndex: number, fieldIndex: number, dir: -1 | 1) {
    mutate((prev) =>
      prev.map((s, j) => {
        if (j !== sectionIndex) return s;
        const next = [...s.fields];
        const target = fieldIndex + dir;
        if (target < 0 || target >= next.length) return s;
        [next[fieldIndex], next[target]] = [next[target]!, next[fieldIndex]!];
        return { ...s, fields: next };
      }),
    );
  }

  function earlierKeys(sectionIndex: number): TriggerField[] {
    const priorIds = sections.slice(0, sectionIndex).flatMap((s) => s.fields.map((f) => f.fieldId));
    return priorIds
      .map((id) => library?.entries.find((f) => f.id === id))
      .filter((f): f is NonNullable<typeof f> => !!f)
      .map((f) => ({ id: f.id, key: f.key, label: f.label, options: f.config.options ?? [] }));
  }

  return (
    <div className="flex min-h-0 flex-1 gap-8">
      <div className="min-w-0 flex-1 overflow-y-auto">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {sections.reduce((n, s) => n + s.fields.length, 0)} field
            {sections.reduce((n, s) => n + s.fields.length, 0) === 1 ? '' : 's'}
            {dirty && <span className="ml-2 text-amber-600 dark:text-amber-500">· unsaved changes</span>}
          </p>
        </div>
        <div className="space-y-6">
          {sections.map((section, si) => (
            <div key={si} className="rounded-lg border bg-card p-5">
              <div className="mb-3 flex items-center gap-2">
                <Input
                  value={section.title}
                  onChange={(e) => updateSection(si, { title: e.target.value })}
                  className="h-8 max-w-xs text-sm font-semibold"
                />
                <div className="flex-1" />
                <button
                  onClick={() => removeSection(si)}
                  disabled={section.fields.length > 0}
                  title={section.fields.length > 0 ? 'Remove its fields first' : 'Remove section'}
                  className="text-muted-foreground hover:text-destructive disabled:opacity-30"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <ConditionEditor
                value={section.showWhen}
                options={earlierKeys(si)}
                onChange={(c) => setSectionCondition(si, c)}
              />
              <div className="mt-4 space-y-3">
                {section.fields.map((sf, fi) => {
                  const def = allDefs?.entries.find((f) => f.id === sf.fieldId);
                  return (
                    <div key={fi} className="rounded-md border bg-muted/20 p-4">
                      <div className="flex items-center gap-2">
                        <div className="flex shrink-0 flex-col">
                          {/* disabled: must sit on the BUTTON — an svg never
                              matches :disabled, so the old styling was inert */}
                          <button
                            onClick={() => moveField(si, fi, -1)}
                            disabled={fi === 0}
                            aria-label="Move up"
                            className="rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-30 disabled:hover:text-muted-foreground"
                          >
                            <ChevronUp className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => moveField(si, fi, 1)}
                            disabled={fi === section.fields.length - 1}
                            aria-label="Move down"
                            className="rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-30 disabled:hover:text-muted-foreground"
                          >
                            <ChevronDown className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <span className="min-w-0 flex-1 break-words text-sm font-medium">
                          {def?.label ?? sf.fieldId}
                        </span>
                        <button
                          onClick={() => setEditingFieldId(editingFieldId === sf.fieldId ? null : sf.fieldId)}
                          className="shrink-0 text-muted-foreground hover:text-foreground"
                          aria-label="Edit field settings"
                          title="Edit label, help text, placeholder, options…"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => removeField(si, fi)}
                          className="shrink-0 text-muted-foreground hover:text-destructive"
                          aria-label="Remove from this form"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 pl-6 text-xs text-muted-foreground">
                        <label className="flex items-center gap-1.5">
                          <input
                            type="checkbox"
                            checked={sf.required}
                            onChange={(e) => updateField(si, fi, { required: e.target.checked })}
                            className="h-3.5 w-3.5 rounded border-input"
                          />
                          Required
                        </label>
                        <label className="flex items-center gap-1.5">
                          <input
                            type="checkbox"
                            checked={sf.filterable}
                            onChange={(e) => updateField(si, fi, { filterable: e.target.checked })}
                            className="h-3.5 w-3.5 rounded border-input"
                          />
                          Filterable
                        </label>
                        <Select
                          value={sf.audience}
                          onChange={(e) =>
                            updateField(si, fi, { audience: e.target.value as 'both' | 'agent-only' })
                          }
                          className="h-7 w-40 text-xs"
                        >
                          <option value="both">Requester + agent</option>
                          <option value="agent-only">Agent only</option>
                        </Select>
                        {/* Step 66: visibility and writability are separate
                            questions. Only meaningful once the requester can
                            see the field at all. */}
                        {sf.audience === 'both' && (
                          <label className="flex items-center gap-1.5">
                            <input
                              type="checkbox"
                              checked={sf.requesterCanEdit !== false}
                              onChange={(e) => updateField(si, fi, { requesterCanEdit: e.target.checked })}
                              className="h-3.5 w-3.5 rounded border-input"
                            />
                            Requester can edit
                          </label>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <FieldTypeToolbar
                shared={sharedCandidates}
                onAttach={(fieldId) => {
                  addFieldToSection(si, fieldId);
                  setEditingFieldId(fieldId);
                }}
                onCreate={async (type, label) => {
                  const key = label
                    .trim()
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '_')
                    .replace(/^_+|_+$/g, '');
                  const res = await fieldsApi.create({
                    key: key || `field_${Date.now()}`,
                    label: label.trim(),
                    type,
                    config: {},
                    // This item's own question, not library content.
                    scope: 'item',
                  });
                  if (res.ok && res.body) {
                    await queryClient.invalidateQueries({ queryKey: qk.fieldDefinitions });
                    addFieldToSection(si, res.body.id);
                    setEditingFieldId(res.body.id);
                  }
                  return res.ok;
                }}
              />
            </div>
          ))}
          <button onClick={addSection} className="flex items-center gap-1 text-sm text-brand hover:underline">
            <Plus className="h-4 w-4" /> Add section
          </button>
        </div>
      </div>

      <aside className="w-96 shrink-0 overflow-y-auto border-l pl-6">
        <div className="mb-2 flex items-center gap-2">
          <Eye className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm font-semibold">Live preview</p>
          <div className="flex-1" />
          <Select
            value={previewSurface}
            onChange={(e) => setPreviewSurface(e.target.value as 'agent' | 'requester')}
            className="h-7 w-28 text-xs"
          >
            <option value="agent">Agent</option>
            <option value="requester">Requester</option>
          </Select>
        </div>
        {!layoutId && <p className="text-xs text-muted-foreground">Save to preview the real form.</p>}
        {layoutId && (
          <FieldRenderer
            fields={resolved?.fields ?? []}
            values={previewValues}
            onChange={(k, v) => setPreviewValues((prev) => ({ ...prev, [k]: v }))}
          />
        )}
      </aside>

      {/* Field properties live in a right slide-over, like the reference's
          side modal — the row's pencil opens it; Escape/backdrop closes. */}
      <Sheet
        open={!!editingField}
        onClose={() => setEditingFieldId(null)}
        title={editingField ? `${TYPE_LABELS[editingField.type]} field: ${editingField.label}` : ''}
      >
        {editingField && (
          <FieldConfigPanel
            field={editingField}
            onClose={() => setEditingFieldId(null)}
            onSaved={() => void queryClient.invalidateQueries({ queryKey: qk.fieldDefinitions })}
          />
        )}
      </Sheet>
    </div>
  );
});

/** One icon per type, matching the reference's toolbar — click creates a
 *  field of that type (name it, done) rather than picking type from a
 *  dropdown first. Types needing no quick affordance here (file, content)
 *  are still reachable via the full config panel after creation if ever
 *  needed; the toolbar covers the types actually used across real forms. */
const TYPE_TOOLBAR: { type: FieldType; icon: typeof Type; label: string }[] = [
  { type: 'text', icon: Type, label: 'Single line text' },
  { type: 'textarea', icon: AlignLeft, label: 'Paragraph' },
  { type: 'richtext', icon: FileText, label: 'Rich text' },
  { type: 'number', icon: Hash, label: 'Number' },
  { type: 'decimal', icon: Percent, label: 'Decimal' },
  { type: 'select', icon: ListFilter, label: 'Dropdown' },
  { type: 'multiselect', icon: ListChecks, label: 'Multi-select' },
  { type: 'radio', icon: CircleDot, label: 'Radio group' },
  { type: 'checkbox', icon: CheckSquare, label: 'Checkbox' },
  { type: 'date', icon: Calendar, label: 'Date' },
  { type: 'datetime', icon: CalendarClock, label: 'Date & time' },
  { type: 'url', icon: Link2, label: 'URL' },
  { type: 'email', icon: Mail, label: 'Email' },
  { type: 'phone', icon: Phone, label: 'Phone' },
  { type: 'person', icon: User, label: 'Person lookup' },
  { type: 'text_list', icon: List, label: 'Repeatable text list' },
  { type: 'file', icon: Paperclip, label: 'File attachment' },
];

function FieldTypeToolbar({
  onCreate,
  shared,
  onAttach,
}: {
  onCreate: (type: FieldType, label: string) => Promise<boolean>;
  /** Existing library fields not already on this layout — Step 67's "Add
   *  shared field", meant for the universal blocks (the on-behalf-of pair,
   *  "Any other information?") that nearly every form reuses. */
  shared: FieldDefinition[];
  onAttach: (fieldId: string) => void;
}) {
  const [pending, setPending] = useState<FieldType | null>(null);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sharedOpen, setSharedOpen] = useState(false);
  // URL state on purpose: the search box inside the "add a shared field"
  // picker. The picker is an overlay on the item editor — it opens, you pick,
  // it closes — and the ITEM is what belongs in the URL.
  const [sharedFilter, setSharedFilter] = useState('');

  async function submitCreate(type: FieldType) {
    if (!label.trim() || busy) return;
    setBusy(true);
    setError(null);
    const ok = await onCreate(type, label);
    setBusy(false);
    if (ok) {
      setPending(null);
      setLabel('');
    } else {
      setError('Could not create that field — try a different label.');
    }
  }

  if (pending) {
    const meta = TYPE_TOOLBAR.find((t) => t.type === pending)!;
    return (
      <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border bg-muted/20 p-2.5">
        <meta.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="shrink-0 text-xs text-muted-foreground">{meta.label} —</span>
        <Input
          autoFocus
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submitCreate(pending);
            if (e.key === 'Escape') {
              setPending(null);
              setError(null);
            }
          }}
          placeholder="Field label"
          className="h-8 w-48 text-xs"
        />
        <Button size="sm" disabled={!label.trim() || busy} onClick={() => void submitCreate(pending)}>
          Add
        </Button>
        <button
          onClick={() => {
            setPending(null);
            setError(null);
          }}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
        {error && <p className="w-full text-xs text-destructive">{error}</p>}
      </div>
    );
  }

  if (sharedOpen) {
    const matches = shared.filter(
      (f) =>
        !sharedFilter.trim() ||
        f.label.toLowerCase().includes(sharedFilter.trim().toLowerCase()) ||
        f.key.toLowerCase().includes(sharedFilter.trim().toLowerCase()),
    );
    return (
      <div className="mt-4 rounded-md border p-3">
        <div className="mb-2 flex items-center gap-2">
          <p className="text-xs font-medium text-muted-foreground">Add an existing field</p>
          <div className="flex-1" />
          <button
            onClick={() => {
              setSharedOpen(false);
              setSharedFilter('');
            }}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
        </div>
        <Input
          autoFocus
          value={sharedFilter}
          onChange={(e) => setSharedFilter(e.target.value)}
          placeholder="Search the field library"
          className="mb-2 h-8 text-xs"
        />
        <div className="max-h-56 divide-y overflow-y-auto rounded-md border">
          {matches.map((f) => (
            <button
              key={f.id}
              onClick={() => {
                onAttach(f.id);
                setSharedOpen(false);
                setSharedFilter('');
              }}
              className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs hover:bg-accent"
            >
              <span className="min-w-0 flex-1 truncate">{f.label}</span>
              <span className="shrink-0 font-mono text-[10px] text-muted-foreground">{f.key}</span>
              <span className="shrink-0 text-[10px] text-muted-foreground">{TYPE_LABELS[f.type]}</span>
            </button>
          ))}
          {matches.length === 0 && (
            <p className="px-2.5 py-3 text-center text-xs text-muted-foreground">
              {shared.length === 0
                ? 'Every library field is already on this form.'
                : 'No library field matches that.'}
            </p>
          )}
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">
          Shared fields keep one definition everywhere — editing the label or choices changes it on every form
          that uses it, and reporting can compare across them.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-md border p-3">
      <div className="mb-2 flex items-center gap-2">
        <p className="text-xs font-medium text-muted-foreground">Add a field</p>
        <div className="flex-1" />
        <button
          onClick={() => setSharedOpen(true)}
          className="flex items-center gap-1 text-xs text-brand hover:underline"
        >
          <Library className="h-3.5 w-3.5" /> Add shared field
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {TYPE_TOOLBAR.map((t) => (
          <button
            key={t.type}
            onClick={() => setPending(t.type)}
            aria-label={`Add ${t.label} field`}
            className="flex items-center gap-1.5 rounded-md border border-transparent px-2.5 py-1.5 text-xs text-muted-foreground hover:border-input hover:bg-accent hover:text-foreground"
          >
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** The field's own SHARED settings (label/help text/placeholder/options) —
 *  distinct from the per-layout required/audience/filterable rail above it.
 *  Editing here changes the field everywhere it's used, which is exactly
 *  the point of a shared library; key and type stay immutable (simply not
 *  offered here), matching the plan's guardrail against rewriting history. */
function FieldConfigPanel({
  field,
  onClose,
  onSaved,
}: {
  field: {
    id: string;
    label: string;
    helpText?: string;
    type: FieldType;
    hasBeenUsed?: number;
    config: {
      placeholder?: string;
      defaultToken?: string;
      options?: SelectOption[];
      allowOther?: boolean;
      peopleFilter?: Condition;
      minLength?: number;
      maxLength?: number;
      min?: number;
      max?: number;
      pattern?: string;
      patternMessage?: string;
      dateOnly?: boolean;
    };
  };
  onClose: () => void;
  onSaved: () => void;
}) {
  const HAS_OPTIONS: FieldType[] = ['select', 'multiselect', 'radio'];
  const HAS_DATA_SOURCE: FieldType[] = ['select', 'multiselect'];
  const HAS_LENGTH: FieldType[] = ['text', 'textarea', 'phone'];
  const HAS_RANGE: FieldType[] = ['number', 'decimal'];
  const typeLocked = !!field.hasBeenUsed;
  const [label, setLabel] = useState(field.label);
  const [helpText, setHelpText] = useState(field.helpText ?? '');
  const [type, setType] = useState<FieldType>(field.type);
  const [placeholder, setPlaceholder] = useState(field.config.placeholder ?? '');
  /** Step 70 — the prefill token, e.g. `{{requester.department}}`. */
  const [defaultToken, setDefaultToken] = useState(
    (field.config as { defaultToken?: string }).defaultToken ?? '',
  );
  const [options, setOptions] = useState<SelectOption[]>(field.config.options ?? []);
  const [allowOther, setAllowOther] = useState(!!field.config.allowOther);
  const [source, setSource] = useState<'static' | 'people'>(field.config.peopleFilter ? 'people' : 'static');
  // URL state on purpose: NOT a filter on this screen — it is the condition
  // being AUTHORED for a people-picker field, i.e. part of the field's config
  // and saved with it. The name collides with the audit's filter pattern.
  const [peopleFilter, setPeopleFilter] = useState<Condition | undefined>(field.config.peopleFilter);
  const [minLength, setMinLength] = useState(field.config.minLength?.toString() ?? '');
  const [maxLength, setMaxLength] = useState(field.config.maxLength?.toString() ?? '');
  const [min, setMin] = useState(field.config.min?.toString() ?? '');
  const [max, setMax] = useState(field.config.max?.toString() ?? '');
  const [pattern, setPattern] = useState(field.config.pattern ?? '');
  const [patternMessage, setPatternMessage] = useState(field.config.patternMessage ?? '');
  const [testValue, setTestValue] = useState('');
  const [dateOnly, setDateOnly] = useState(!!field.config.dateOnly);
  const [busy, setBusy] = useState(false);

  // Is the pattern valid, and does the test value pass it? Computed live so
  // an admin never ships a regex they haven't seen accept AND reject.
  let patternState: 'invalid' | 'pass' | 'fail' | null = null;
  if (pattern.trim()) {
    try {
      const re = new RegExp(pattern);
      patternState = testValue ? (re.test(testValue) ? 'pass' : 'fail') : null;
    } catch {
      patternState = 'invalid';
    }
  }

  return (
    <div className="space-y-2.5">
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">Label</label>
        <Input value={label} onChange={(e) => setLabel(e.target.value)} />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">Help text</label>
        <Input
          value={helpText}
          onChange={(e) => setHelpText(e.target.value)}
          placeholder="Shown under the label"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">
          Type {typeLocked && '(in use on a live form — locked)'}
        </label>
        <Select value={type} onChange={(e) => setType(e.target.value as FieldType)} disabled={typeLocked}>
          {FIELD_TYPES.map((t) => (
            <option key={t} value={t}>
              {TYPE_LABELS[t]}
            </option>
          ))}
        </Select>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">Placeholder</label>
        <Input
          value={placeholder}
          onChange={(e) => setPlaceholder(e.target.value)}
          placeholder="Shown inside the empty field"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">
          Prefill <span className="font-normal">— from the person the form is for</span>
        </label>
        {/* A PICKER, not a text box. The rule is "must be a placeholder", and
            the honest way to enforce a closed set is to offer the closed set:
            a free field plus a validator invites people to type "London" and
            then argue with the error. */}
        <Select value={defaultToken} onChange={(e) => setDefaultToken(e.target.value)}>
          <option value="">Leave blank</option>
          {PREFILL_TOKENS.map((t) => (
            <option key={t.token} value={`{{${t.token}}}`}>
              {t.label}
            </option>
          ))}
        </Select>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Filled in from the directory when the form opens; blank when it has nothing.
        </p>
      </div>
      {HAS_DATA_SOURCE.includes(type) && (
        <div className="flex gap-3 rounded-md border p-2">
          <label className="flex items-center gap-1.5 text-xs">
            <input
              type="radio"
              checked={source === 'static'}
              onChange={() => setSource('static')}
              className="h-3.5 w-3.5"
            />
            <ListChecks className="h-3.5 w-3.5 text-muted-foreground" /> Custom dropdown
          </label>
          <label className="flex items-center gap-1.5 text-xs">
            <input
              type="radio"
              checked={source === 'people'}
              onChange={() => setSource('people')}
              className="h-3.5 w-3.5"
            />
            <Database className="h-3.5 w-3.5 text-muted-foreground" /> Select from directory
          </label>
        </div>
      )}
      {HAS_OPTIONS.includes(type) && source === 'static' && (
        <div>
          <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <ArrowUpDown className="h-3 w-3" /> Choices
          </label>
          <OptionsEditor options={options} onChange={setOptions} />
          <label className="mt-1.5 flex items-center gap-1.5 text-xs">
            <input
              type="checkbox"
              checked={allowOther}
              onChange={(e) => setAllowOther(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-input"
            />
            Allow "Other" (free text)
          </label>
        </div>
      )}
      {HAS_DATA_SOURCE.includes(type) && source === 'people' && (
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Directory filter</label>
          <PeopleFilterEditor filter={peopleFilter} onChange={setPeopleFilter} />
        </div>
      )}
      {(HAS_LENGTH.includes(type) || HAS_RANGE.includes(type) || type === 'datetime') && (
        <div className="rounded-md border bg-muted/10 p-2.5">
          <p className="mb-2 text-xs font-medium text-muted-foreground">Validation</p>
          {HAS_LENGTH.includes(type) && (
            <div className="space-y-2">
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="mb-1 block text-[11px] text-muted-foreground">Min length</label>
                  <Input
                    type="number"
                    value={minLength}
                    onChange={(e) => setMinLength(e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="flex-1">
                  <label className="mb-1 block text-[11px] text-muted-foreground">Max length</label>
                  <Input
                    type="number"
                    value={maxLength}
                    onChange={(e) => setMaxLength(e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-[11px] text-muted-foreground">
                  Pattern (regular expression)
                </label>
                <Input
                  value={pattern}
                  onChange={(e) => setPattern(e.target.value)}
                  placeholder="^\\+?[0-9 ]{7,15}$"
                  className="h-8 font-mono text-xs"
                />
                {patternState === 'invalid' && (
                  <p className="mt-1 text-[11px] text-destructive">That isn't a valid regular expression.</p>
                )}
              </div>
              {pattern.trim() && patternState !== 'invalid' && (
                <>
                  <div>
                    <label className="mb-1 block text-[11px] text-muted-foreground">
                      Error message when it fails
                    </label>
                    <Input
                      value={patternMessage}
                      onChange={(e) => setPatternMessage(e.target.value)}
                      placeholder="Please include the country code, e.g. +44"
                      className="h-8 text-xs"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] text-muted-foreground">Test expression</label>
                    <div className="flex items-center gap-2">
                      <Input
                        value={testValue}
                        onChange={(e) => setTestValue(e.target.value)}
                        placeholder="Try a sample answer…"
                        className="h-8 flex-1 text-xs"
                      />
                      {patternState === 'pass' && (
                        <span className="shrink-0 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                          passes
                        </span>
                      )}
                      {patternState === 'fail' && (
                        <span className="shrink-0 text-[11px] font-medium text-destructive">rejected</span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          {HAS_RANGE.includes(type) && (
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="mb-1 block text-[11px] text-muted-foreground">Minimum</label>
                <Input
                  type="number"
                  value={min}
                  onChange={(e) => setMin(e.target.value)}
                  className="h-8 text-xs"
                />
              </div>
              <div className="flex-1">
                <label className="mb-1 block text-[11px] text-muted-foreground">Maximum</label>
                <Input
                  type="number"
                  value={max}
                  onChange={(e) => setMax(e.target.value)}
                  className="h-8 text-xs"
                />
              </div>
            </div>
          )}
          {type === 'datetime' && (
            <label className="flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={dateOnly}
                onChange={(e) => setDateOnly(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-input"
              />
              Date only (hide the time picker)
            </label>
          )}
        </div>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <Button size="sm" variant="outline" onClick={onClose}>
          Close
        </Button>
        <Button
          size="sm"
          disabled={!label.trim() || busy}
          onClick={async () => {
            setBusy(true);
            if (patternState === 'invalid') {
              setBusy(false);
              return;
            }
            // Start from the STORED config so keys this panel doesn't manage
            // survive a save (the old rebuild-from-scratch wiped them), then
            // apply — or delete — every managed key explicitly.
            const config: Record<string, unknown> = { ...field.config };
            const setOrDelete = (key: string, v: unknown, keep: boolean) => {
              if (keep) config[key] = v;
              else delete config[key];
            };
            setOrDelete('placeholder', placeholder.trim(), !!placeholder.trim());
            setOrDelete('defaultToken', defaultToken, !!defaultToken);
            setOrDelete(
              'options',
              options.filter((o) => o.label.trim()),
              HAS_OPTIONS.includes(type) && source === 'static',
            );
            setOrDelete('allowOther', allowOther, HAS_OPTIONS.includes(type) && source === 'static');
            setOrDelete(
              'peopleFilter',
              peopleFilter,
              HAS_DATA_SOURCE.includes(type) && source === 'people' && !!peopleFilter,
            );
            const lengthy = HAS_LENGTH.includes(type);
            setOrDelete('minLength', Number(minLength), lengthy && minLength.trim() !== '');
            setOrDelete('maxLength', Number(maxLength), lengthy && maxLength.trim() !== '');
            setOrDelete('pattern', pattern.trim(), lengthy && !!pattern.trim());
            setOrDelete(
              'patternMessage',
              patternMessage.trim(),
              lengthy && !!pattern.trim() && !!patternMessage.trim(),
            );
            const rangey = HAS_RANGE.includes(type);
            setOrDelete('min', Number(min), rangey && min.trim() !== '');
            setOrDelete('max', Number(max), rangey && max.trim() !== '');
            setOrDelete('dateOnly', true, type === 'datetime' && dateOnly);
            const res = await fieldsApi.update(field.id, {
              label: label.trim(),
              helpText: helpText.trim(),
              ...(typeLocked ? {} : { type }),
              config,
            });
            setBusy(false);
            if (res.ok) {
              onSaved();
              onClose();
            }
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

export interface TriggerField {
  id: string;
  key: string;
  label: string;
  options: SelectOption[];
}

/** Trigger values are picked from the field's OWN real choices — never free
 *  text (Kai: "the only show if X equals X needs to be multiple choice & a
 *  choice, not free text" — a typo'd value would silently never match).
 *  Multiple choices selected becomes `in`, matching the reference's tag-
 *  style "Show this section for [FM ×] [DAB ×]"; one becomes a plain `eq`. */
function ConditionEditor({
  value,
  options,
  onChange,
}: {
  value: Condition | undefined;
  options: TriggerField[];
  onChange: (c: Condition | undefined) => void;
}) {
  if (!options.length) return null;
  const active = value && (value.op === 'eq' || value.op === 'in') ? value : undefined;
  const trigger = options.find((o) => o.key === active?.fieldKey);
  const selectedValues: (string | number)[] = active
    ? active.op === 'in'
      ? active.value
      : [active.value as string | number]
    : [];

  function setTrigger(key: string) {
    if (!key) {
      onChange(undefined);
      return;
    }
    onChange({ op: 'in', fieldKey: key, value: [] });
  }

  function toggleValue(v: string) {
    if (!trigger) return;
    const next = selectedValues.includes(v) ? selectedValues.filter((x) => x !== v) : [...selectedValues, v];
    if (next.length === 0) onChange({ op: 'in', fieldKey: trigger.key, value: [] });
    else if (next.length === 1) onChange({ op: 'eq', fieldKey: trigger.key, value: next[0]! });
    else onChange({ op: 'in', fieldKey: trigger.key, value: next as string[] });
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      <span className="shrink-0 text-muted-foreground">Show this section for</span>
      <Select
        value={trigger?.key ?? ''}
        onChange={(e) => setTrigger(e.target.value)}
        className="h-7 w-40 text-xs"
      >
        <option value="">(always shown)</option>
        {options.map((o) => (
          <option key={o.id} value={o.key}>
            {o.label}
          </option>
        ))}
      </Select>
      {trigger &&
        (trigger.options.length ? (
          <div className="flex flex-wrap items-center gap-1">
            {trigger.options
              .filter((o) => !o.archived)
              .map((o) => {
                const active2 = selectedValues.includes(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => toggleValue(o.value)}
                    className={`rounded-full border px-2 py-0.5 text-xs ${active2 ? 'border-brand bg-brand/10 text-brand' : 'text-muted-foreground hover:bg-accent'}`}
                  >
                    {o.label}
                  </button>
                );
              })}
          </div>
        ) : (
          <span className="text-muted-foreground">
            ("{trigger.label}" has no fixed choices to trigger from — give it options first)
          </span>
        ))}
    </div>
  );
}
